import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '../../../i18n';
import MarketingHeader from './MarketingHeader';
import MarketingFooter from './MarketingFooter';
import CtaBand from '../Home/CtaBand';
import { AUTHOR, AUTHOR_URL } from './author';
import { AuthorBox, AuthorByline } from './AuthorByline';
import '../../../styles/marketing.css';
import '../../../styles/marketing-guide.css';

export interface FaqItem {
  q: string;
  a: string;
}

interface GuideLayoutProps {
  eyebrow: string; // ex. « Guide · Invitations »
  title: string; // H1, repris dans les données structurées
  updated: string; // AAAA-MM-JJ
  published: string; // AAAA-MM-JJ
  readingMinutes: number;
  lead: React.ReactNode;
  topCta?: React.ReactNode; // encadré d'appel à l'action sous l'introduction
  toc: Array<{ id: string; label: string }>;
  faq: FaqItem[];
  children: React.ReactNode; // sections de l'article (avant la FAQ)
}

// Mise en page commune des articles du site vitrine : en-tête, introduction,
// sommaire, sections, FAQ, données structurées (article + FAQ) et pied de page.
const GuideLayout: React.FC<GuideLayoutProps> = ({
  eyebrow,
  title,
  updated,
  published,
  readingMinutes,
  lead,
  topCta,
  toc,
  faq,
  children,
}) => {
  const t = useT();
  const url = `https://www.please-rsvp.com${useLocation().pathname}`;
  const updatedLabel = new Date(`${updated}T12:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const ld = JSON.stringify([
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      mainEntityOfPage: url,
      dateModified: updated,
      datePublished: published,
      inLanguage: 'fr',
      author: { '@type': 'Person', name: AUTHOR.name, url: AUTHOR_URL },
      publisher: { '@type': 'Organization', name: 'RSVP Please' },
      image: 'https://www.please-rsvp.com/og-image.png',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://www.please-rsvp.com/' },
        { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://www.please-rsvp.com/guides' },
        { '@type': 'ListItem', position: 3, name: title, item: url },
      ],
    },
  ]);

  return (
    <div className="mk-page">
      <MarketingHeader>
        <Link to="/login" className="mkt-btn ghost">
          {t('Se connecter')}
        </Link>
        <Link to="/signup" className="mkt-btn primary mk-header-signup">
          {t('Créer mon espace')}
        </Link>
      </MarketingHeader>

      <main className="mk-main" lang="fr">
        <article className="mg-article mk-container">
          <header className="mg-article-head">
            <span className="mk-eyebrow">{eyebrow}</span>
            <h1>{title}</h1>
            <AuthorByline updatedLabel={updatedLabel} readingMinutes={readingMinutes} />
            <p className="mg-article-lead">{lead}</p>
          </header>

          {topCta}

          <nav className="mg-toc" aria-labelledby="mg-toc-title">
            <p id="mg-toc-title" className="mg-toc-title">
              Sommaire
            </p>
            <ol>
              {toc.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.label}</a>
                </li>
              ))}
            </ol>
          </nav>

          {children}

          <section id="faq">
            <h2>Questions fréquentes</h2>
            <div className="mg-faq">
              {faq.map(({ q, a }) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>

          <AuthorBox />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: ld }} />
        </article>

        <CtaBand />
      </main>

      <MarketingFooter />
    </div>
  );
};

export default GuideLayout;
